/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-get, ember/require-computed-property-dependencies, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Service, { service } from '@ember/service';
// Ember Imports
import config from 'linced-app/config/environment';
import EmberObject, { computed } from '@ember/object';
// External imports
import moment from 'moment';
// Internal imports
import ExpulsionPostType from 'linced-app/enums/expulsion-post-type';
import ExpulsionStage from 'linced-app/enums/expulsion-stage';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import PostStatus from '@linc-technologies/linced-common/enums/post-status';
import PostType from '@linc-technologies/linced-common/enums/post-type';
import { isEmpty } from '@ember/utils';
import { onAttachmentUpload } from '@linc-technologies/linced-common/utils/uppy';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import { processTemplate } from '@linc-technologies/linced-common/utils/post-templates';
import { SuspensionCategory } from '@linc-technologies/linced-common/enums/suspensions';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';

export default class ExpulsionsService extends Service {
  // Services
  @service
  behaviourDates;

  @service
  dates;

  @service
  fetch;

  @service
  options;

  @service
  session;

  @service
  store;

  @service
  terms;

  @service
  uppy;

  // Options
  @optionAlias('nswDOE:delInfo:name')
  delName;

  @optionAlias('hero:behaviours:doeContact')
  doePhoneNumber;

  // Properties
  @tracked expulsion = null;

  @tracked prevSuspensionEndDate = '';
  @tracked principal = null;
  @tracked suspensionLetterPage = null;
  @tracked suspensionPostPage = null;
  @tracked attachments = [];
  @tracked teachers = [];

  @computed('expulsion.{suspensionStartDate,suspensionExtensionDelApproval}')
  get suspensionStartDateError() {
    return this.behaviourDates.getExpulsionDateErrors(
      this.expulsion,
      'suspensionStartDate',
      this.expulsion.get('suspensionStartDate'),
    );
  }

  @computed(
    'expulsion.{suspensionStartDate,suspensionEndDate,suspensionExtensionDelApproval,suspension.id,noticeDate}',
  )
  get suspensionEndDateError() {
    if (!isEmpty(this.get('expulsion.suspension.id'))) {
      // freshly converted suspensions get an additional 15 days from the date of the written notice
      return this.behaviourDates.getExpulsionDateErrors(
        this.expulsion,
        'noticeDate',
        this.expulsion.get('suspensionEndDate'),
      );
    }
    return this.behaviourDates.getExpulsionDateErrors(
      this.expulsion,
      'suspensionEndDate',
      this.expulsion.get('suspensionEndDate'),
    );
  }

  @computed(
    'expulsion.{earlyReturnDate,suspensionStartDate,suspensionEndDate,stage}',
  )
  get earlyReturnDateError() {
    return this.behaviourDates.getExpulsionDateErrors(
      this.expulsion,
      'earlyReturnDate',
      this.expulsion.get('earlyReturnDate'),
    );
  }

  @computed('expulsion.caregiverMeetingDate')
  get caregiverMeetingDateError() {
    return this.behaviourDates.getExpulsionDateErrors(
      this.expulsion,
      'caregiverMeetingDate',
      this.expulsion.get('caregiverMeetingDate'),
    );
  }

  @computed('expulsion.reviewDate')
  get reviewDateError() {
    return this.behaviourDates.getExpulsionDateErrors(
      this.expulsion,
      'reviewDate',
      this.expulsion.get('reviewDate'),
    );
  }

  @computed('expulsion.expulsionStartDate')
  get expulsionStartDateError() {
    return this.behaviourDates.getExpulsionDateErrors(
      this.expulsion,
      'expulsionStartDate',
      this.expulsion.get('expulsionStartDate'),
    );
  }

  @computed(
    'expulsion.{title,suspensionStartDate,suspensionEndDate,caregiverMeetingDate,nominatedSchoolPerson,caregivers,factualDetailsOfBehavioursOfConcern,specificDetailsOfDocuments,reviewDate,reasonForDecision,expulsionStartDate,caregivers.[]}',
  )
  get error() {
    if (
      isEmpty(this.expulsion.caregivers) ||
      this.expulsion.caregivers.length <= 0
    ) {
      return true;
    }
    if (isEmpty(this.get('expulsion.nominatedSchoolPerson.id'))) {
      return true;
    }
    if (isEmpty(this.expulsion.title)) {
      return true;
    }
    if (isEmpty(this.expulsion.suspensionStartDate)) {
      return true;
    }
    if (isEmpty(this.expulsion.suspensionEndDate)) {
      return true;
    }
    if (isEmpty(this.expulsion.caregiverMeetingDate)) {
      return true;
    }
    if (isEmpty(this.expulsion.reviewDate)) {
      return true;
    }
    if (isEmpty(this.expulsion.factualDetailsOfBehavioursOfConcern)) {
      return true;
    }

    switch (this.expulsion.stage) {
      case ExpulsionStage.NOTICE_OF_DECISION_TO_EXPEL:
        if (isEmpty(this.expulsion.reviewDate)) {
          return true;
        }
        if (isEmpty(this.expulsion.reasonForDecision)) {
          return true;
        }
        break;
      case ExpulsionStage.EXPELLED:
        if (isEmpty(this.expulsion.expulsionStartDate)) {
          return true;
        }
        break;
    }

    return false;
  }

  @computed(
    'error',
    'suspensionStartDateError',
    'suspensionEndDateError',
    'caregiverMeetingDateError',
    'reviewDateError',
    'expulsionStartDateError',
    'earlyReturnDateError',
  )
  get isValid() {
    return (
      !this.error &&
      isEmpty(this.suspensionStartDateError) &&
      isEmpty(this.suspensionEndDateError) &&
      (isEmpty(this.caregiverMeetingDateError) ||
        this.caregiverMeetingDateError ===
          this.behaviourDates.caregiverNonSchoolDayWarning) &&
      isEmpty(this.reviewDateError) &&
      isEmpty(this.expulsionStartDateError) &&
      isEmpty(this.earlyReturnDateError)
    );
  }

  @computed('principal')
  get principalDetails() {
    // No principal found, just return an empty object for now
    if (isEmpty(this.principal)) {
      return EmberObject.create({});
    }
    return {
      name: this.principal.fullName,
      signature: this.principal.firstName[0] + this.principal.lastName[0],
    };
  }

  @computed(
    'expulsion.{caregivers.[],nominatedSchoolPerson,factualDetailsOfBehavioursOfConcern,specificDetailsOfDocuments,reasonForDecision}',
    'delName',
    'doePhoneNumber',
    'principalDetails',
  )
  get noticesOfPotentialExpulsion() {
    if (isEmpty(this.suspensionLetterPage)) {
      return [];
    }

    // caregivers is an async hasMany, whose PromiseManyArray has no map().
    return resolved(this.expulsion.caregivers).map((caregiver) => {
      return {
        value: processTemplate(
          this.getNoticeOfPotentialExpulsionSeriousBehaviourOfConcern(),
          {
            school: this.expulsion.get('school'),
            person: this.expulsion.get('learner'),
            caregiver: caregiver,
            behaviour: {
              delName: this.delName,
              principal: this.principalDetails,
              nominatedSchoolPerson: this.expulsion.get(
                'nominatedSchoolPerson',
              ),
              doePhoneNumber: this.doePhoneNumber,
            },
            expulsion: this.expulsion,
          },
        ),
      };
    });
  }

  @computed(
    'expulsion.{caregivers.[],nominatedSchoolPerson,factualDetailsOfBehavioursOfConcern,specificDetailsOfDocuments,reasonForDecision}',
    'delName',
    'doePhoneNumber',
    'principalDetails',
  )
  get noticesOfExpulsion() {
    if (isEmpty(this.suspensionLetterPage)) {
      return [];
    }

    // caregivers is an async hasMany, whose PromiseManyArray has no map().
    return resolved(this.expulsion.caregivers).map((caregiver) => {
      return {
        value: processTemplate(
          this.getNoticeOfExpulsionSeriousBehaviourOfConcern(),
          {
            school: this.expulsion.get('school'),
            person: this.expulsion.get('learner'),
            caregiver: caregiver,
            behaviour: {
              delName: this.delName,
              principal: this.principalDetails,
              nominatedSchoolPerson: this.expulsion.get(
                'nominatedSchoolPerson',
              ),
              doePhoneNumber: this.doePhoneNumber,
            },
            expulsion: this.expulsion,
          },
        ),
      };
    });
  }

  init() {
    super.init(...arguments);

    this.teachers = this.store
      .peekAll('person')
      .filter((person) => person.type === PersonType.STAFF);
  }

  // initialise sets the service up for an expulsion dialog. It resolves once
  // the expulsion's existing attachments have loaded.
  async initialise(
    expulsion,
    principal,
    suspensionLetterPage,
    suspensionPostPage,
  ) {
    // severe issues with suspension dates when setting suspensions across multiple
    // years unless we load the records in first
    this.dates.loadYear(moment().add(1, 'year').format('YYYY'), true);

    this.expulsion = expulsion;
    this.principal = principal;
    this.suspensionLetterPage = suspensionLetterPage;
    this.suspensionPostPage = suspensionPostPage;
    this.prevSuspensionEndDate = expulsion.get('suspensionEndDate');

    // Point the shared uppy at this list straight away, so files added while
    // the existing ones load land here rather than in another flow.
    let call = (this._initialiseCall = {});
    this.attachments = [];
    this.uppy.setupAttachments((file, upload, mediaType) => {
      let attachment = onAttachmentUpload(
        this.store,
        2,
        this.get('session.school'),
        this.get('session.person'),
        file,
        upload,
        mediaType,
      );
      this.attachments = [...this.attachments, attachment];
    });

    // resolve the async hasMany relationship upfront.
    // attachments is an async hasMany: load the existing files, then put them
    // ahead of anything uploaded in the meantime.
    let existing = (await expulsion.attachments).map((record) => ({ record }));
    if (this._initialiseCall !== call) {
      // initialise ran again (e.g. didReceiveAttrs) while this one waited;
      // only the newest call may fill the list.
      return;
    }
    this.attachments = [...existing, ...this.attachments];
  }

  getLettersPreview() {
    return this.expulsion.stage ===
      ExpulsionStage.WRITTEN_NOTICE_OF_POTENTIAL_EXPULSION
      ? this.noticesOfPotentialExpulsion
      : this.noticesOfExpulsion;
  }

  // Attachments
  removeAttachment(attachment) {
    this.attachments = this.attachments.filter((a) => a !== attachment);
  }

  // createOrUpdatePostForExpulsion is used to create or update a given post of type for an expulsion.
  // It returns the expulsion save promise.
  async createOrUpdatePostForExpulsion(type, post, subject, content, date) {
    const pageIdMap = {
      [ExpulsionPostType.EXPULSION_POST]: this.suspensionPostPage,
      [ExpulsionPostType.NOTICE_OF_POTENTIAL_EXPULSION]:
        this.suspensionLetterPage,
      [ExpulsionPostType.NOTICE_OF_EXPULSION]: this.suspensionLetterPage,
    };

    const pageId = pageIdMap[type];
    if (isEmpty(pageId)) {
      return;
    }
    if (isEmpty(post)) {
      post = this.store.createRecord('posts/post', {
        postType: PostType.POST,
        status: PostStatus.DRAFT,
        isSickbay: type === ExpulsionPostType.EXPULSION_POST, // Letter posts should be editable, expulsion summary posts should not be.
        schoolId: this.get('session.school'),
        displayDate: date,
        authorId: this.get('session.person'),
        personId: await this.get('expulsion.learnerId'),
        pageId,
        content,
        subject,
        jurisdiction: Jurisdiction.AU_NSW,
        isSuspensionPosts: type === ExpulsionPostType.EXPULSION_POST,
        isSuspensionLetters:
          type === ExpulsionPostType.NOTICE_OF_EXPULSION ||
          type === ExpulsionPostType.NOTICE_OF_POTENTIAL_EXPULSION,
      });
    } else {
      post.set('content', content);
    }

    return await post.save();
  }

  async expulsionSavePipeline(letters) {
    // Kees if you complain about this being here and mucking up
    // your nice looking pipeline i s2g

    // PSA: dont override save functions
    this.expulsion.set(
      'suspensionDuration',
      this.behaviourDates.getNumSchoolDaysBetweenDates(
        this.expulsion.suspensionStartDate,
        this.expulsion.suspensionEndDate,
      ),
    );

    await this.createOrUpdateSuspensionForExpulsion();

    // Set suspension attendance
    this.setAttendanceForSuspension();

    // Update summary
    await this.createOrUpdateSummaryPost();

    // Send letters
    await this.createOrUpdateLetters(letters);

    await this.expulsion.save();
  }

  async createOrUpdateSuspensionForExpulsion() {
    let expulsion = await this.expulsion;
    let incident = await this.expulsion.get('incident');
    let suspension = await this.expulsion.get('suspension');

    // Build Suspension Record.
    const params = {
      schoolId: expulsion.get('schoolId'),
      reason: incident.get('category'),
      writtenReason: incident.get('customCategory'),
      learnerId: incident.learnerId,
      recordedDate: expulsion.get('date'),
      behaviourIncident: incident,
      nominatedContact: expulsion.get('nominatedSchoolPerson'),
      jurisdiction: expulsion.get('jurisdiction'),
      category: SuspensionCategory.SUSPENSION,
      caregivers: expulsion.get('caregivers'),
      startDate: expulsion.get('suspensionStartDate'),
      endDate: expulsion.get('suspensionEndDate'),
      earlyReturnDate: expulsion.get('earlyReturnDate'),
      duration: expulsion.get('suspensionDuration'),
      consultationDate: expulsion.get('caregiverMeetingDate'),
      reviewDate: expulsion.get('reviewDate'),
      description: expulsion.get('factualDetailsOfBehavioursOfConcern'),
      outcomeDetails: expulsion.get('reasonForDecision'),
      extended: expulsion.get('suspensionExtensionDelApproval'),
      expulsion,
    };

    if (expulsion.get('stage') === ExpulsionStage.EXPELLED) {
      params.earlyReturnDate = '';
      params.endDate = expulsion.get('expulsionStartDate');
    }

    if (isEmpty(suspension)) {
      suspension = this.store.createRecord('attendance/suspension', params);
    }
    suspension.setProperties(params);

    return await suspension.save();
  }

  async createOrUpdateSummaryPost() {
    let content = '';
    if (this.expulsion.title) {
      content += '<strong>Title: </strong>' + this.expulsion.title + '<br>';
    }

    if (this.expulsion.category) {
      content +=
        '<strong>Category: </strong>' + this.expulsion.displayCategory + '<br>';
    }

    if (this.expulsion.stage) {
      content +=
        '<strong>Stage: </strong>' + this.expulsion.displayStage + '<br>';
    }

    if (this.expulsion.get('nominatedSchoolPerson.fullName')) {
      content +=
        '<strong>Nominated School Person: </strong>' +
        this.expulsion.get('nominatedSchoolPerson.fullName') +
        '<br>';
    }

    if (this.expulsion.noticeDate) {
      content +=
        '<strong>Written Notice of Potential Expulsion Date: </strong>' +
        moment(this.expulsion.noticeDate, 'YYYY-MM-DD').format(
          'dddd, Do MMMM YYYY',
        ) +
        '<br>';
    }

    if (this.expulsion.caregiverMeetingDate) {
      content +=
        '<strong>Caregiver Meeting Date: </strong>' +
        moment(this.expulsion.caregiverMeetingDate, 'YYYY-MM-DD').format(
          'dddd, Do MMMM YYYY',
        ) +
        '<br>';
    }

    if (this.expulsion.reviewDate) {
      content +=
        '<strong>Review Date: </strong>' +
        moment(this.expulsion.reviewDate, 'YYYY-MM-DD').format(
          'dddd, Do MMMM YYYY',
        ) +
        '<br>';
    }

    if (this.expulsion.expulsionStartDate) {
      content +=
        '<strong>Expulsion Date: </strong>' +
        moment(this.expulsion.expulsionStartDate, 'YYYY-MM-DD').format(
          'dddd, Do MMMM YYYY',
        ) +
        '<br>';
    }

    if (!isEmpty(this.expulsion.earlyReturnDate)) {
      content +=
        '<strong>Suspension Early Return Date:</strong>' +
        moment(this.expulsion.earlyReturnDate, 'YYYY-MM-DD').format(
          'dddd, Do MMMM YYYY',
        ) +
        '<br>';
    }

    content += '<br>';

    if (this.expulsion.factualDetailsOfBehavioursOfConcern) {
      content +=
        '<strong>Factual Details of Behaviours of Concern: </strong>' +
        this.expulsion.factualDetailsOfBehavioursOfConcern +
        '<br>';
    }

    if (this.expulsion.specificDetailsOfDocuments) {
      content +=
        '<strong>Specific Details of Documents: </strong>' +
        this.expulsion.specificDetailsOfDocuments +
        '<br>';
    }

    if (this.expulsion.reasonForDecision) {
      content +=
        '<strong>Reason for Decision: </strong>' +
        this.expulsion.reasonForDecision +
        '<br>';
    }

    if (this.expulsion.outcome) {
      content +=
        '<strong>Outcome: </strong>' + this.expulsion.displayOutcome + '<br>';
    }

    let post = await this.expulsion.get('expulsionPost');

    post = await this.createOrUpdatePostForExpulsion(
      ExpulsionPostType.EXPULSION_POST,
      post,
      `${this.expulsion.get('learner.fullName')} Expulsion Summary`,
      content,
      this.expulsion.noticeDate,
    );

    if (isEmpty(post)) {
      return;
    }

    this.set('expulsion.expulsionPost', post);
  }

  async createOrUpdateLetters(letters) {
    if (isEmpty(letters)) {
      return;
    }

    // Update notice of potential expulsion
    if (
      this.expulsion.stage >=
      ExpulsionStage.WRITTEN_NOTICE_OF_POTENTIAL_EXPULSION
    ) {
      const posts = await this.get('expulsion.noticeOfPotentialExpulsions');
      const numLetters = letters.length;

      for (let i = 0; i < numLetters; i++) {
        const letterContent = letters[i].value;
        const existingPost = i < posts.length ? posts[i] : null;

        if (!isEmpty(existingPost)) {
          continue;
        }

        // Create notice of potential expulsion letter post.
        const noticeOfPotentialExpulsion =
          await this.createOrUpdatePostForExpulsion(
            ExpulsionPostType.NOTICE_OF_POTENTIAL_EXPULSION,
            null,
            `${this.expulsion.get(
              'learner.fullName',
            )} Notice of Potential Expulsion`,
            letterContent,
            this.expulsion.noticeDate,
          );

        if (isEmpty(noticeOfPotentialExpulsion)) {
          continue;
        }

        posts.push(noticeOfPotentialExpulsion);

        let person = await this.get('session.person');
        let school = await this.get('session.school');

        // Attach the attachments to the post and save.
        let updatedMediaRecords = [];
        // resolve the async hasMany relationship upfront.
        let existingMediaRecords = (
          await noticeOfPotentialExpulsion.media
        ).slice();
        for (const attachment of this.attachments) {
          // Find existing record by matching on name and size, this should catch updated files with the same name.
          let existingRecord = existingMediaRecords.find(
            (record) =>
              record.fileName === attachment.record.fileName &&
              record.fileSize === attachment.record.fileSize,
          );

          // If we already have an existing record we do not need to create a new one, continue
          if (!isEmpty(existingRecord)) {
            updatedMediaRecords.push(existingRecord);
            continue;
          }

          // Time to create a new record
          // Set the post prop on each attachment to the letter post for the
          // current caregiver. We can do this because we have already
          // saved the post.
          let record = this.store.createRecord('media', {
            post: noticeOfPotentialExpulsion,
            fileName: attachment.record.fileName,
            fileSize: attachment.record.fileSize,
            image: attachment.record.image,
            mediaSource: attachment.record.mediaSource,
            mediaType: attachment.record.mediaType,
            mimeType: attachment.record.mimeType,
            sharedWith: attachment.record.sharedWith,
            storageKey: attachment.record.storageKey,
            school,
            person,
            video: attachment.record.video,
          });

          // Save the record. This also gets us an id
          await record.save();
          updatedMediaRecords.push(record);
        }

        // Delete any media records for attachments that got removed.
        existingMediaRecords.forEach((existingRecord) => {
          if (
            isEmpty(
              updatedMediaRecords.find(
                (record) =>
                  record.fileName === existingRecord.fileName &&
                  record.fileSize === existingRecord.fileSize,
              ),
            )
          ) {
            existingRecord.destroyRecord().catch(() => {
              this.toaster.error('Error destroying attachment!');
            });
          }
        });

        // Set the array of media on the post
        noticeOfPotentialExpulsion.set('media', updatedMediaRecords);
        // Re-save the post
        await noticeOfPotentialExpulsion.save();
      }

      // We need to store some form of media record on the expulsion
      // so let's just use the first post's media array.
      // We could use any of the post's media records since they are just duplicates.
      if (posts[0]) {
        // resolve the async hasMany relationship upfront.
        // Due to the async relationship, indexing directly on explusion.noticeOfPotentialExpulsions
        // (posts.media) gave undefined, wiping attachments.
        this.expulsion.set('attachments', (await posts[0].media).slice());
      }
    }

    // Update notice of expulsion
    if (
      this.expulsion.stage >= ExpulsionStage.NOTICE_OF_DECISION_TO_EXPEL &&
      this.expulsion.stage !== ExpulsionStage.NOT_EXPELLED
    ) {
      const posts = await this.get('expulsion.noticeOfExpulsions');
      const numLetters = letters.length;

      for (let i = 0; i < numLetters; i++) {
        const letterContent = letters[i].value;
        const existingPost = i < posts.length ? posts[i] : null;

        if (!isEmpty(existingPost)) {
          continue;
        }

        const noticeOfExpulsion = await this.createOrUpdatePostForExpulsion(
          ExpulsionPostType.NOTICE_OF_EXPULSION,
          null,
          `${this.expulsion.get('learner.fullName')} Notice of Expulsion`,
          letterContent,
          this.expulsion.noticeDate,
        );

        if (isEmpty(noticeOfExpulsion)) {
          continue;
        }

        posts.push(noticeOfExpulsion);
      }
    }

    // Clear attachments array
    this.attachments = [];
  }

  // Letters
  // Hard coded letter templates for suspensions and behaviours DoE legal letters
  getNoticeOfPotentialExpulsionSeriousBehaviourOfConcern() {
    return `
      <p>
        <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="3" data-placeholder-for="core:firstName"><span class="material-icons">label_outline</span>Caregiver: First Name</span> <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="3" data-placeholder-for="core:lastName"><span class="material-icons">label_outline</span>Caregiver: Last Name</span><br>
        <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="3" data-placeholder-for="core:address1"><span class="material-icons">label_outline</span>Caregiver: Address Line 1</span><br>
        <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="3" data-placeholder-for="core:address2"><span class="material-icons">label_outline</span>Caregiver: Address Line 2</span><br>
        <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="3" data-placeholder-for="core:address3"><span class="material-icons">label_outline</span>Caregiver: Address Line 3</span><br>
        <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="3" data-placeholder-for="core:address4"><span class="material-icons">label_outline</span>Caregiver: Address Line 4</span><br>
        <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="3" data-placeholder-for="core:postcode"><span class="material-icons">label_outline</span>Caregiver: Post Code</span><br>
        <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="3" data-placeholder-for="core:emailAddress"><span class="material-icons">label_outline</span>Caregiver: Email address</span><br>
      </p>
      <p>Dear <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="3" data-placeholder-for="core:firstName"><span class="material-icons">label_outline</span>Caregiver: First Name</span> <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="3" data-placeholder-for="core:lastName"><span class="material-icons">label_outline</span>Caregiver: Last Name</span>,</p>
      <p>This letter is to inform you that I, along with my Director, Educational Leadership am considering expelling,&nbsp;<span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="1" data-placeholder-for="core:firstNameLegal"><span class="material-icons">label_outline</span>First name (legal)</span> <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="1" data-placeholder-for="core:lastNameLegal"><span class="material-icons">label_outline</span>Last name (legal)</span> for serious behaviour or behaviour/s of concern where behaviour interventions and risk management strategies, including suspensions, have not been successful. This has involved the following serious behaviour or behaviour/s of concern:</p>
      <p><span class="hero-template-placeholder" data-placeholder-type="expulsion" data-placeholder-for="expulsion_factual_details_of_concern"><span class="material-icons">label_outline</span>Factual Details of Concern</span></p>
      <p>I have placed your child on suspension while a decision is being made to expel. &nbsp;These actions are consistent with the NSW Department of Education Student Behaviour Policy and Suspension and Expulsion Procedures.</p>
      <p>Someone from the school will contact you to arrange a formal meeting as soon as possible so that we can discuss this matter prior to any decision to expel being made. At this meeting I will explain the reasons for considering expulsion, the implications of expulsion and your appeal rights. If this is difficult, please let us know and we will work out how best to support you. &nbsp;Any meetings can occur face to face, by telephone or online, based on the format that will give the best chance of a positive and productive discussion.</p>
      <p>If you wish, you can bring a support person with you to any meetings. A support person can provide you and/or your child with practical or emotional support. There is further information on the role of a support person at&nbsp;<a href="https://education.nsw.gov.au/content/dam/main-education/going-to-a-public-school/media/documents/translated-documents-/information-about-support-persons-and-advocates/english-support-persons-and-advocates.pdf" target="_blank" rel="nofollow noopener">https://education.nsw.gov.au/content/dam/main-education/going-to-a-public-school/media/documents/translated-documents-/information-about-support-persons-and-advocates/english-support-persons-and-advocates.pdf</a>.</p>
      <p>You will have seven (7) school days from this notice to consider and respond to the information shared with you before a final decision is made.&nbsp;</p>
      <p>It is expected that your child will continue to engage with learning while this decision is being made. Your child will be provided with some learning materials. The school may contact you or your child during the suspension to check on their learning progress. The nominated person from the school is <span class="hero-template-placeholder" data-placeholder-type="behaviour" data-placeholder-for="nominated_school_person_name"><span class="material-icons">label_outline</span>Nominated School Person Name</span>.</p>
      <p>As school principal, I have the responsibility to:</p>
      <ul>
        <li>convene a formal meeting with yourself, (student name) and optional support person as soon as possible</li>
        <li>arrange an interpreter for you if necessary</li>
        <li>provide you with access to:&nbsp;
          <ul>
            <li>learning materials or program&nbsp;</li>
            <li>the Student Behaviour Policy and Suspension and Expulsion procedures. Available at <a href="https://education.nsw.gov.au/policy-library/policies/pd-2006-0316" target="_blank" rel="nofollow noopener">https://education.nsw.gov.au/policy-library/policies/pd-2006-0316</a></li>
            <li>wellbeing tip sheet including information about support services. Available at <a href="https://education.nsw.gov.au/content/dam/main-education/student-wellbeing/attendance-behaviour-and-engagement/student-behaviour/behaviour-support-toolkit/parents/Wellbeing_Tip_Sheet_2023.pdf" target="_blank" rel="nofollow noopener">https://education.nsw.gov.au/content/dam/main-education/student-wellbeing/attendance-behaviour-and-engagement/student-behaviour/behaviour-support-toolkit/parents/Wellbeing_Tip_Sheet_2023.pdf</a></li>
            <li>parent and carer information, including information about the serious behaviour or behaviour/s of concern, expulsion process and appeal rights and processes. Available at <a href="https://education.nsw.gov.au/schooling/school-community/attendance-behaviour-and-engagement/behaviour-support-toolkit/support-for-parents" target="_blank" rel="nofollow noopener">https://education.nsw.gov.au/schooling/school-community/attendance-behaviour-and-engagement/behaviour-support-toolkit/support-for-parents</a></li>
            <li>an appeal form. Available at <a href="https://education.nsw.gov.au/content/dam/main-education/student-wellbeing/attendance-behaviour-and-engagement/student-behaviour/behaviour-support-toolkit/parents/Form_to_appeal_of_suspension_or_expulsion_decision.pdf" target="_blank" rel="nofollow noopener">https://education.nsw.gov.au/content/dam/main-education/student-wellbeing/attendance-behaviour-and-engagement/student-behaviour/behaviour-support-toolkit/parents/Form_to_appeal_of_suspension_or_expulsion_decision.pdf</a></li>
          </ul>
        </li>
      </ul>
      <p>Please contact the school office if you would like a printed copy of any of these documents.</p>
      <p>As a parent or carer, you have a responsibility to:&nbsp;</p>
      <ul>
        <li>communicate with the school as soon as possible to confirm a formal meeting to discuss this notice of potential expulsion</li>
        <li>engage with the school to manage your child&rsquo;s behaviour and plan appropriate behaviour support solutions together, if required let the school if you require an interpreter and we will arrange it</li>
        <li>arrange a <a href="https://education.nsw.gov.au/content/dam/main-education/going-to-a-public-school/media/documents/translated-documents-/information-about-support-persons-and-advocates/english-support-persons-and-advocates.pdf" target="_blank" rel="nofollow noopener">support person</a> to accompany you to the meeting if you wish</li>
        <li>provide adequate supervision, care and safety for your child while they are suspended as the school does not have duty of care during the suspension&nbsp;</li>
        <li>ensure that your child does not enter onto school grounds without my permission, except to attend the scheduled formal meeting.</li>
      </ul>
      <p>If you need an interpreter to assist you to contact the school, please call the Telephone Interpreter Service on 131 450 and ask for an interpreter in your language. Tell the operator the phone number you want to ring, and the operator will get an interpreter on the line to help you with your conversation. You will not be charged for this service.&nbsp;</p>
      <p>Yours sincerely<br><div id="principal_signature"><span class="hero-template-placeholder" data-placeholder-type="behaviour" data-placeholder-for="principal_sig"><span class="material-icons">label_outline</span>Principal Signature</span></div><br><span class="hero-template-placeholder" data-placeholder-type="behaviour" data-placeholder-for="principal_name"><span class="material-icons">label_outline</span>Principal Name</span><br>Principal<br><span class="hero-template-placeholder" data-placeholder-type="basic" data-placeholder-for="date_short"><span class="material-icons">label_outline</span>Date (short)</span></p>
      <p><span class="hero-template-placeholder" data-placeholder-type="behaviour" data-placeholder-for="del_name"><span class="material-icons">label_outline</span>DEL Name</span><br>Director, Educational Leadership<br><span class="hero-template-placeholder" data-placeholder-type="basic" data-placeholder-for="date_short"><span class="material-icons">label_outline</span>Date (short)</span></p>

      <div id="return_slip">
        <p><strong>Notice of potential expulsion for serious behaviour/s of concern response &ndash; please return via email or to school office</strong></p>
        <p>[&nbsp; ]&nbsp; I have discussed this matter with my child and have noted the notice of potential expulsion for serious behaviour/s of concern.<br>[&nbsp; ] &nbsp;I will meet with the school as soon as possible to discuss this matter<br>[&nbsp; ]&nbsp; (optional) I will bring a support person to attend the meeting with me.&nbsp;</p>
        <table style="width:100%;border-collapse:collapse;border-width:1px;border-color:#000;border-style:solid;padding:4px;" border="1><colgroup><col style="width:50%"><col style="width:50%"></colgroup>
          <tbody>
            <tr>
              <td style="border-width:1px;border-color:#000;border-style:solid;padding:4px;">Student Name:</td>
              <td style="border-width:1px;border-color:#000;border-style:solid;padding:4px;">&nbsp;</td>
            </tr>
            <tr>
              <td style="border-width:1px;border-color:#000;border-style:solid;padding:4px;">Parent/Carer Name:</td>
              <td style="border-width:1px;border-color:#000;border-style:solid;padding:4px;">&nbsp;</td>
            </tr>
            <tr>
              <td style="border-width:1px;border-color:#000;border-style:solid;padding:4px;">Parent/Carer Signature:</td>
              <td style="border-width:1px;border-color:#000;border-style:solid;padding:4px;">&nbsp;</td>
            </tr>
            <tr>
              <td style="border-width:1px;border-color:#000;border-style:solid;padding:4px;">Date:</td>
              <td style="border-width:1px;border-color:#000;border-style:solid;padding:4px;">&nbsp;</td>
            </tr>
            <tr>
              <td style="border-width:1px;border-color:#000;border-style:solid;padding:4px;">Parent/Carer&rsquo;s preferred contact method and details:</td>
              <td style="border-width:1px;border-color:#000;border-style:solid;padding:4px;">&nbsp;</td>
            </tr>
          </tbody>
        </table>
        <p>Please return this slip to: <span class="hero-template-placeholder" data-placeholder-type="behaviour" data-placeholder-for="principal_name"><span class="material-icons">label_outline</span>Principal Name</span> <span class="hero-template-placeholder" data-placeholder-type="basic" data-placeholder-for="school_email"><span class="material-icons">label_outline</span>School email address</span></p>
      </div>`;
  }

  getNoticeOfExpulsionSeriousBehaviourOfConcern() {
    return `<p>
        <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="3" data-placeholder-for="core:firstName"><span class="material-icons">label_outline</span>Caregiver: First Name</span> <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="3" data-placeholder-for="core:lastName"><span class="material-icons">label_outline</span>Caregiver: Last Name</span><br>
        <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="3" data-placeholder-for="core:address1"><span class="material-icons">label_outline</span>Caregiver: Address Line 1</span><br>
        <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="3" data-placeholder-for="core:address2"><span class="material-icons">label_outline</span>Caregiver: Address Line 2</span><br>
        <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="3" data-placeholder-for="core:address3"><span class="material-icons">label_outline</span>Caregiver: Address Line 3</span><br>
        <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="3" data-placeholder-for="core:address4"><span class="material-icons">label_outline</span>Caregiver: Address Line 4</span><br>
        <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="3" data-placeholder-for="core:postcode"><span class="material-icons">label_outline</span>Caregiver: Post Code</span><br>
        <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="3" data-placeholder-for="core:emailAddress"><span class="material-icons">label_outline</span>Caregiver: Email address</span><br>
      </p>
      <p>Dear <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="3" data-placeholder-for="core:firstName"><span class="material-icons">label_outline</span>Caregiver: First Name</span> <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="3" data-placeholder-for="core:lastName"><span class="material-icons">label_outline</span>Caregiver: Last Name</span>,</p>
      <p>This letter is to inform you that we have considered all the facts and information in relation to the serious behaviours of concern listed in the notice of potential expulsion and discussed in the formal meeting with you on <span class="hero-template-placeholder" data-placeholder-type="expulsion" data-placeholder-for="caregiver_meeting_date"><span class="material-icons">label_outline</span>Caregiver: First Name</span>.&nbsp;We have made the decision to expel <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="1" data-placeholder-for="core:firstNameLegal"><span class="material-icons">label_outline</span>First name (legal)</span> <span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="1" data-placeholder-for="core:lastNameLegal"><span class="material-icons">label_outline</span>Last name (legal)</span> from&nbsp;<span class="hero-template-placeholder" data-placeholder-type="basic" data-placeholder-for="school_name"><span class="material-icons">label_outline</span>School name</span> for serious behaviour/s of concern where behaviour interventions and risk management strategies, including suspensions, have not been successful.</p>
      <p>This expulsion is consistent with the NSW Department of Education Student Behaviour Policy and Suspension and Expulsion Procedures, available at <a href="https://education.nsw.gov.au/policy-library/policies/pd-2006-0316" target="_blank" rel="nofollow noopener">https://education.nsw.gov.au/policy-library/policies/pd-2006-0316</a>. Please contact the school office if you would like a printed copy.&nbsp;</p>
      <p>My Director, Educational Leadership and I will attempt to arrange an alternative educational placement which is appropriate to the needs of your child within fifteen (15) school days from this notice of expulsion. I will work with the local education office to help identify an appropriate placement where your child can access curriculum while being safely accommodated.&nbsp;</p>
      <p>Your child will remain suspended while this occurs. During this time, we will continue to provide a learning program and keep in touch with you about the next steps. Your child may only re-enrol at this school with the approval of the Executive Director, Public Schools. The school can also provide you with details of support services available to you and your child.</p>
      <p>If you consider that correct procedures have not been followed or an unfair decision has been made you may lodge an appeal. An appeal form is available at <a href="https://education.nsw.gov.au/content/dam/main-education/student-wellbeing/attendance-behaviour-and-engagement/student-behaviour/behaviour-support-toolkit/parents/Form_to_appeal_of_suspension_or_expulsion_decision.pdf" target="_blank" rel="nofollow noopener">https://education.nsw.gov.au/content/dam/main-education/student-wellbeing/attendance-behaviour-and-engagement/student-behaviour/behaviour-support-toolkit/parents/Form_to_appeal_of_suspension_or_expulsion_decision.pdf</a>. If you need help lodging an appeal, you may contact the local education office via 131 536 to discuss the appeal process.&nbsp;</p>
      <p>You can access more information for parents and carers online, including information about expulsion and appeal rights and processes in the Behaviour support toolkit parents and carers webpage available at <a href="https://education.nsw.gov.au/student-wellbeing/attendance-behaviour-and-engagement/behaviour-support-toolkit/support-for-parents" target="_blank" rel="nofollow noopener">https://education.nsw.gov.au/student-wellbeing/attendance-behaviour-and-engagement/behaviour-support-toolkit/support-for-parents</a>.</p>
      <p>I will contact you to arrange a meeting as soon as possible. If you wish, you can bring a support person with you to attend this meeting. A support person can provide you and/or&nbsp;<span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="1" data-placeholder-for="core:firstNameLegal"><span class="material-icons">label_outline</span>First name (legal)</span>&nbsp;with practical or emotional support. The meeting can occur in person, <br>online or by telephone. Please let me know if you need an interpreter and I will arrange one.</p>
      <p>If you need an interpreter to assist you to contact the school, please call the Telephone Interpreter Service on 131 450 and ask for an interpreter in your language. Tell the operator the phone number you want to ring, and the operator will get an interpreter on the line to help you with your conversation. You will not be charged for this service.</p>
      <p>Yours sincerely<br><div id="principal_signature"><span class="hero-template-placeholder" data-placeholder-type="behaviour" data-placeholder-for="principal_sig"><span class="material-icons">label_outline</span>Principal Signature</span></div><br><span class="hero-template-placeholder" data-placeholder-type="behaviour" data-placeholder-for="principal_name"><span class="material-icons">label_outline</span>Principal Name</span><br>Principal<br><span class="hero-template-placeholder" data-placeholder-type="basic" data-placeholder-for="date_short"><span class="material-icons">label_outline</span>Date (short)</span></p>
      <p><span class="hero-template-placeholder" data-placeholder-type="behaviour" data-placeholder-for="del_name"><span class="material-icons">label_outline</span>DEL Name</span><br>Director, Educational Leadership<br><span class="hero-template-placeholder" data-placeholder-type="basic" data-placeholder-for="date_short"><span class="material-icons">label_outline</span>Date (short)</span></p>`;
  }

  // Attendance

  // Clear marks if suspension end date is moved back
  clearSuspensionMarks(suspensionEndDay) {
    // We want to grab all attendance records from the day after
    // the early return date and clear the suspended mark from them all
    let startDate = moment(suspensionEndDay, 'YYYY-MM-DD')
      .add(1, 'day')
      .format('YYYY-MM-DD');

    this.store
      .query('attendance/record', {
        personId: this.expulsion.get('learner.id'),
        dateFrom: startDate,
        dateTo: this.prevSuspensionEndDate,
      })
      .then((records) => {
        let promises = records.map((record) => {
          if (record.code === 'E') {
            return record.destroyRecord().catch(() => {
              this.toaster.error('Error clearing attendance data!');
            });
          }
        });

        return Promise.all(promises);
      });
  }

  findExistingRecord(records, dateId) {
    return records.find((record) => {
      return record.date_id === dateId;
    });
  }

  setAttendanceForSuspension() {
    let suspensionStartDay = this.expulsion.suspensionStartDate;
    let suspensionEndDay = this.expulsion.suspensionEndDate;
    let code = 'E'; // Code for suspended in NSW
    let session = this.session;
    let learner = this.expulsion.get('learner');

    if (!isEmpty(this.expulsion.earlyReturnDate)) {
      suspensionEndDay = this.expulsion.earlyReturnDate;
    }

    if (!isEmpty(this.expulsion.expulsionStartDate)) {
      suspensionEndDay = this.expulsion.expulsionStartDate;
    }

    if (isEmpty(suspensionStartDay) || isEmpty(suspensionEndDay)) {
      return;
    }

    if (suspensionEndDay < this.prevSuspensionEndDate) {
      this.clearSuspensionMarks(suspensionEndDay);
    }

    let dates = this.behaviourDates.calculateSchoolDaysInRange(
      suspensionStartDay,
      suspensionEndDay,
      true,
    );

    return this.store
      .query('attendance/record', {
        personId: this.expulsion.get('learner.id'),
        dateFrom: suspensionStartDay,
        dateTo: suspensionEndDay,
      })
      .then((existingRecords) => {
        let newRecords = [];
        let updatedRecords = [];

        dates.forEach((date) => {
          let day = this.dates.dayForDate(date);

          if (isEmpty(day)) {
            return;
          }

          let heroDate = this.dates.getDate(date);
          let periodIds = day.get('periodIds');

          if (isEmpty(periodIds)) {
            return;
          }

          let period = this.store.peekRecord(
            'timetable/period',
            periodIds.objectAt(0),
          );

          let effectiveFrom = moment(
            `${date}T${period.get('startTime')}`,
            'YYYY-MM-DDTHH:mm',
          ).unix();
          let newRecord = {
            authorId: session.get('person.id'),
            code: code,
            date,
            dateId: heroDate.id,
            effectiveFrom,
            homeroomId: learner.get('homeroom.id'),
            notes: 'Suspended',
            personId: learner.get('id'),
            periodId: period.id,
            schoolId: session.get('activeSchool'),
            movements: [
              {
                time: effectiveFrom,
                code: code,
                authorId: session.get('person.id'),
              },
            ],
          };

          let existingRecord = this.findExistingRecord(
            existingRecords,
            heroDate.id,
          );

          if (existingRecord) {
            let record = newRecord;
            record.id = existingRecord.id;

            updatedRecords.push(record);
            return;
          }

          newRecords.push(newRecord);
        });
        let promises = [];

        if (newRecords.length > 0) {
          let options = {
            data: JSON.stringify({ records: newRecords }),
          };

          promises.push(
            this.fetch
              .post(
                `${config.APP.apiHost}/attendance/${config.APP.apiPath}/records/:bulk`,
                options,
              )
              .catch((err) => {
                console.error(err);
              }),
          );
        }

        if (updatedRecords.length > 0) {
          let options = {
            data: JSON.stringify({ records: updatedRecords }),
          };

          promises.push(
            this.fetch
              .post(
                `${config.APP.apiHost}/attendance/${config.APP.apiPath}/records/:bulkupdate`,
                options,
              )
              .catch((err) => {
                console.error(err);
              }),
          );
        }

        return Promise.all(promises);
      });
  }
}
